/**
 * Pure converters from the legacy `bridgeReducers` top-level state maps
 * (`state.organizations`, `state.locations`, `state.resources`, `state.pilots`,
 * `state.instructors`) into the canonical `data` shape that
 * `CalendarFilterBar` + `FilterMultiSelect` expect.
 *
 * Extracted from `MonthV2Filter.js` so the container can stay a thin wrapper
 * and so the shape mapping is unit-testable in isolation if later needed.
 *
 * Legacy bridge entities still carry legacy field names (`locationID`,
 * `pilotSurname`, `organizationUnique`, …); CalendarFilterBar expects the
 * shortened canonical names (`id`, `surname`, `organizationUnique`).
 */

function toArray(source) {
	if (!source) return [];
	if (Array.isArray(source)) return source.filter(Boolean);
	return Object.values(source).filter(Boolean);
}

/**
   * Filter out organizations the user should not see in the org dropdown.
   * `/api/v1/user/organizations` returns `status: 'active'|'overdue'|...`
   * + a boolean `overdue` flag + `planExpiresDate`. `trash` is NOT on this
   * endpoint but kept as a safety check in case another bridge injects it.
   * Missing/unknown fields → visible (prefer false-positives).
   */
  function isInactiveOrg(org) {
        if (!org) return true;
        if (typeof org.status === 'string' && org.status !== 'active') return true;
        if (org.overdue === true || org.overdue === 1 || org.overdue === '1') return true;
        if (org.trash === true || org.trash === 1 || org.trash === '1') return true;
        return false;
  }

export function adaptOrganizations(source) {
	return toArray(source)
		.filter(org => !isInactiveOrg(org))
		.map(org => ({
			id: org.organizationID ?? org.id,
			unique: org.organizationUnique ?? org.unique,
			name: org.organizationName ?? org.name
		}));
}

export function adaptLocations(source) {
	return toArray(source).map(loc => ({
		id: loc.locationID ?? loc.id,
		name: loc.locationName ?? loc.name,
		organizationUnique: loc.organizationUnique,
		organizationName: loc.organizationName
	}));
}

export function adaptResources(source, fallbackOrgUnique = null, fallbackOrgName = null) {
        return toArray(source)
                // Symfony `/resource` returns all resources; instructors have
                // `type.name` containing "instructor" and belong on the
                // dedicated Instructors filter. Keep only non-instructor items
                // so the Resources dropdown doesn't duplicate them.
                .filter(item => {
                        if (!item) return false;
                        const t = typeof item.type === 'object'
                                ? (item.type && item.type.name) || ''
                                : String(item.type || '');
                        return !t.toLowerCase().includes('instructor');
                })
                .map(res => ({
                        id: res.resourceID ?? res.id,
                        name: res.resourceName ?? res.name,
                        // Symfony `/api/v1/org/{unique}/resource` returns nested
                        // `location: {id, name}`; legacy bridge was flat. Support both.
                        locationID: res.locationID ?? res.location?.id,
                        locationName: res.locationName ?? res.location?.name,
                        // Legacy refs endpoint returns single-org data without explicit
                        // `organizationUnique`/`organizationName` per item — fall back
                        // to the current org so cascade filter (sub-epic 10.5) matches
                        // the auto-selected org and the group label isn't "unknown".
                        organizationUnique: res.organizationUnique ?? fallbackOrgUnique,
                        organizationName: res.organizationName ?? fallbackOrgName
                }));
  }

export function adaptPilots(source, fallbackOrgUnique = null, fallbackOrgName = null) {
        return toArray(source).map(p => ({
                id: p.pilotID ?? p.id,
                // Symfony `OrganizationUserResponseDto` returns `name` + `surname`;
                // legacy bridge variants may use `pilotName`/`pilotSurname`.
                // Tolerate `firstName`/`lastName`/`userSurname` as defensive
                // fallbacks so the sort key is never blank.
                name: p.pilotName ?? p.name ?? p.firstName ?? '',
                surname: p.pilotSurname ?? p.surname ?? p.lastName ?? p.userSurname ?? '',
                locationID: p.locationID,
                locationName: p.locationName,
                organizationUnique: p.organizationUnique ?? fallbackOrgUnique,
                organizationName: p.organizationName ?? fallbackOrgName
        }));
  }

/**
 * Instructors bridge stores raw resource entities keyed by
 * `${locationID}-${resourceID}`. Only resources with `type` containing
 * "instructor" belong on the instructor filter (Rem 7 parity).
 */
export function adaptInstructors(source, fallbackOrgUnique = null, fallbackOrgName = null) {
        return toArray(source)
                .filter(item => {
                        if (!item) return false;
                        const t = typeof item.type === 'object'
                                ? (item.type && item.type.name) || ''
                                : String(item.type || '');
                        return t.toLowerCase().includes('instructor');
                })
                .map(item => ({
                        id: item.resourceID ?? item.id,
                        name: item.name,
                        surname: item.surname,
                        locationID: item.locationID ?? item.location?.id,
                        locationName: item.locationName ?? item.location?.name,
                        organizationUnique: item.organizationUnique ?? fallbackOrgUnique,
                        organizationName: item.organizationName ?? fallbackOrgName
                }));
  }
